import DownloadIcon from '@mui/icons-material/Download';
import UnfoldMoreIcon from '@mui/icons-material/KeyboardArrowDown';
import UnfoldLessIcon from '@mui/icons-material/KeyboardArrowUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableRowsIcon from '@mui/icons-material/TableRows';
import Menu from '@mui/material/Menu';
import TableCell from '@mui/material/TableCell';
import { useQuery } from '@tanstack/react-query';
import { reportsService } from 'api';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { DataTable, TableScrollBox } from 'shared/components/DataTable';
import type { DataTableColumn } from 'shared/components/DataTable';
import { queryKeys } from 'shared/constants/queryKeys';
import { svgToPngBase64 } from 'shared/utils/pdfBuilder';

import {
  ColumnHeaderSpan,
  ExportMenu,
  ExportMenuIcon,
  SubGroupName,
  SubGroupRow,
  SubGroupTitleCell,
  SummaryClientName,
  SummaryColorDot,
  SummaryProjectName,
  SummaryTitleCell,
  WeeklyDateHeaderBox,
  WeeklyDateHeaderDay,
  WeeklyDateHeaderDate,
  WeeklyDurationValue,
  WeeklyEmptyValue,
  WeeklyExpandedDurationCell,
  WeeklyExpandedTotalCell,
  WeeklyProjectCountChip,
  WeeklyTabRoot,
  WeeklyTotalValue,
  expandIconStyle,
} from '../reports.styles';
import type { WeeklyTableRow, ExportSummary } from '../utils/exportHelpers';
import {
  handleWeeklyExportCSV,
  handleWeeklyExportExcel,
  handleWeeklyExportPDFv2,
} from '../utils/exportHelpers';
import { formatDuration, generateDateRange, transformWeeklyData } from '../utils/reportsUtils';

interface WeeklyTabProps {
  startDate: string;
  endDate: string;
  filters: {
    userIds?: string[];
    clientIds?: string[];
    projectIds?: string[];
    billable?: boolean | null;
    hasDescription?: boolean;
    descriptionSearch?: string;
  };
  projects: Array<{ id: string; name: string; color: string }>;
  clients: Array<{ id: string; name: string }>;
  users?: Array<{ id: string; name: string }>;
  exportAnchorEl?: HTMLElement | null;
  onExportMenuClose?: () => void;
}

export const WeeklyTab: React.FC<WeeklyTabProps> = ({
  startDate,
  endDate,
  filters,
  projects,
  clients,
  users = [],
  exportAnchorEl,
  onExportMenuClose,
}) => {
  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(new Set());

  const handleExportMenuClose = () => {
    onExportMenuClose?.();
  };

  const apiParams = {
    from: startDate,
    to: endDate,
    userIds: filters.userIds?.join(','),
    projectIds: filters.projectIds?.join(','),
    clientIds: filters.clientIds?.join(','),
    isBillable: filters.billable == null ? undefined : filters.billable,
    hasDescription: filters.hasDescription,
    descriptionSearch: filters.descriptionSearch || undefined,
  };

  const { data: weeklyData, isLoading } = useQuery({
    queryKey: queryKeys.reports.weekly(apiParams),
    queryFn: () => reportsService.getWeeklyReport(apiParams).then((res: any) => res.data.data),
    enabled: !!startDate,
  });

  // Generate date columns from startDate to endDate
  const dates = React.useMemo(() => generateDateRange(startDate, endDate), [startDate, endDate]);

  const totalDuration = weeklyData?.weekTotalDurationSecs ?? 0;
  const billableDuration = weeklyData?.weekBillableDurationSecs ?? 0;
  const amount = weeklyData?.weekAmountCents ?? 0;

  const weeklyRows: WeeklyTableRow[] = React.useMemo(
    () => transformWeeklyData(weeklyData?.groups ?? [], projects, clients, users),
    [weeklyData, projects, clients, users],
  );

  // Expand/collapse helpers
  const allProjectIds = React.useMemo(
    () => new Set(weeklyRows.filter((r) => r.userRows.length >= 1).map((r) => r.id)),
    [weeklyRows],
  );
  const isAllExpanded =
    allProjectIds.size > 0 && Array.from(allProjectIds).every((id) => expandedProjectIds.has(id));

  const handleToggleProject = (id: string) => {
    setExpandedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExpandAll = () => setExpandedProjectIds(new Set(allProjectIds));
  const handleCollapseAll = () => setExpandedProjectIds(new Set());

  const handleExportCSV = () => {
    const summary: ExportSummary = { totalDuration, billableDuration, amount };
    handleWeeklyExportCSV(weeklyRows, dates, summary, startDate, endDate);
    handleExportMenuClose();
  };

  const handleExportExcel = () => {
    const summary: ExportSummary = { totalDuration, billableDuration, amount };
    handleWeeklyExportExcel(weeklyRows, dates, summary, startDate, endDate);
    handleExportMenuClose();
  };

  const handleExportPDF = async () => {
    let logoBase64: string | undefined;
    try {
      const svgRes = await fetch('/ZeLogLogo-192.svg');
      const svgText = await svgRes.text();
      const png = await svgToPngBase64(svgText, 128);
      if (png) logoBase64 = png;
    } catch (_) {
      // logo is optional — proceed without it
    }
    handleWeeklyExportPDFv2({
      rows: weeklyRows,
      dates,
      summary: { totalDuration, billableDuration, amount },
      startDate,
      endDate,
      logoBase64,
    });
    handleExportMenuClose();
  };

  const columns: DataTableColumn<WeeklyTableRow>[] = [
    {
      id: 'title',
      label: (
        <ColumnHeaderSpan>
          {allProjectIds.size > 0 &&
            (isAllExpanded ? (
              <UnfoldLessIcon
                fontSize="medium"
                style={expandIconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCollapseAll();
                }}
                titleAccess="Collapse all"
              />
            ) : (
              <UnfoldMoreIcon
                fontSize="medium"
                style={expandIconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandAll();
                }}
                titleAccess="Expand all"
              />
            ))}
          Project
        </ColumnHeaderSpan>
      ),
      render: (row) => (
        <SummaryTitleCell>
          {row.userRows.length >= 1 && (
            <WeeklyProjectCountChip
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleToggleProject(row.id);
              }}
            >
              {row.userRows.length}
            </WeeklyProjectCountChip>
          )}
          <SummaryColorDot dotcolor={row.color} />
          <span>
            <SummaryProjectName>{row.projectName}</SummaryProjectName>
            {row.clientName && ' - '}
            <SummaryClientName>{row.clientName}</SummaryClientName>
          </span>
        </SummaryTitleCell>
      ),
    },
    ...dates.map((date) => ({
      id: date,
      label: (
        <WeeklyDateHeaderBox>
          <WeeklyDateHeaderDay>{dayjs(date).format('ddd')}</WeeklyDateHeaderDay>
          <WeeklyDateHeaderDate>{dayjs(date).format('MMM D')}</WeeklyDateHeaderDate>
        </WeeklyDateHeaderBox>
      ),
      align: 'right' as const,
      width: 145,
      render: (row: WeeklyTableRow) => {
        const secs = row.dailyDurations[date] ?? 0;
        return secs > 0 ? (
          <WeeklyDurationValue>{formatDuration(secs)}</WeeklyDurationValue>
        ) : (
          <WeeklyEmptyValue>-</WeeklyEmptyValue>
        );
      },
    })),
    {
      id: 'total',
      label: 'Total',
      align: 'right' as const,
      width: 100,
      render: (row) => <WeeklyTotalValue>{formatDuration(row.totalDurationSecs)}</WeeklyTotalValue>,
    },
  ];

  return (
    <WeeklyTabRoot>
      {/* Export menu — triggered from hero Export button */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 100 } } }}
      >
        <ExportMenu onClick={handleExportCSV}>
          <ExportMenuIcon>
            <TableRowsIcon />
          </ExportMenuIcon>
          Export CSV
        </ExportMenu>
        <ExportMenu onClick={handleExportExcel}>
          <ExportMenuIcon>
            <DownloadIcon />
          </ExportMenuIcon>
          Export Excel
        </ExportMenu>
        <ExportMenu onClick={handleExportPDF}>
          <ExportMenuIcon>
            <PictureAsPdfIcon />
          </ExportMenuIcon>
          Export PDF
        </ExportMenu>
      </Menu>

      <TableScrollBox>
        <DataTable<WeeklyTableRow>
          columns={columns}
          rows={weeklyRows}
          getRowId={(row) => row.id}
          loading={isLoading}
          emptyMessage="No data for the selected date and filters."
          borderless
          headerFontSize="11px"
          headerWeight={500}
          headerColor="#5A5578"
          rowHoverColor="rgba(110, 79, 247, 0.03)"
          renderExpandedRow={(row) => {
            if (!expandedProjectIds.has(row.id) || row.userRows.length < 1) return null;
            return (
              <>
                {row.userRows.map((userRow, idx) => (
                  <SubGroupRow key={userRow.userId ?? idx} depth={1}>
                    {/* Name cell */}
                    <TableCell>
                      <SubGroupTitleCell depth={1}>
                        <SubGroupName>{userRow.userName}</SubGroupName>
                      </SubGroupTitleCell>
                    </TableCell>
                    {/* Per-date cells */}
                    {dates.map((date) => {
                      const secs = userRow.dailyDurations[date] ?? 0;
                      return (
                        <TableCell key={date} align="right">
                          {secs > 0 ? (
                            <WeeklyExpandedDurationCell>
                              {formatDuration(secs)}
                            </WeeklyExpandedDurationCell>
                          ) : (
                            <WeeklyEmptyValue>-</WeeklyEmptyValue>
                          )}
                        </TableCell>
                      );
                    })}
                    {/* Total cell */}
                    <TableCell align="right">
                      <WeeklyExpandedTotalCell>
                        {formatDuration(userRow.totalDurationSecs)}
                      </WeeklyExpandedTotalCell>
                    </TableCell>
                  </SubGroupRow>
                ))}
              </>
            );
          }}
        />
      </TableScrollBox>
    </WeeklyTabRoot>
  );
};
